import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import {
  ProjectWasmModule,
  WasmModuleFunction,
} from '../../shared/interfaces/wasm-module.interface';

export interface WasmModuleDetailDialogData {
  module: ProjectWasmModule;
}

@Component({
  selector: 'app-wasm-module-detail-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTabsModule,
    MatChipsModule,
    MatExpansionModule,
    MatSnackBarModule,
    ClipboardModule,
  ],
  templateUrl: './wasm-module-detail-dialog.component.html',
  styleUrls: ['./wasm-module-detail-dialog.component.scss'],
})
export class WasmModuleDetailDialogComponent {
  readonly dialogRef = inject<MatDialogRef<WasmModuleDetailDialogComponent>>(MatDialogRef);
  private readonly data = inject<WasmModuleDetailDialogData>(MAT_DIALOG_DATA);
  private readonly clipboard = inject(Clipboard);
  private readonly snackBar = inject(MatSnackBar);

  readonly module = signal<ProjectWasmModule>(this.data.module);
  readonly manifest = computed(() => this.module().manifest);
  readonly functions = computed(() => this.manifest().functions ?? []);
  readonly hasHelp = computed(() => !!this.manifest().help);
  readonly hasExamples = computed(
    () => (this.manifest().examples?.length ?? 0) > 0
  );

  copyCode(code: string): void {
    this.clipboard.copy(code);
    this.snackBar.open('Copied to clipboard', '', { duration: 2000 });
  }

  toPythonName(name: string): string {
    return name.replaceAll('-', '_');
  }

  toCamelCase(name: string): string {
    return name.replace(/[-_]([a-z])/g, (_, c) => c.toUpperCase());
  }

  pythonSignature(func: WasmModuleFunction): string {
    if (func.inputs.length === 0) return `${func.name}()`;
    const params = func.inputs
      .map((i) => (i.required ? i.name : `${i.name}=...`))
      .join(', ');
    return `${func.name}(${params})`;
  }

  jsSignature(func: WasmModuleFunction): string {
    const jsName = this.toCamelCase(func.name);
    if (func.inputs.length === 0) return `${jsName}()`;
    const params = func.inputs.map((i) => this.toCamelCase(i.name)).join(', ');
    return `${jsName}({ ${params} })`;
  }

  pythonExample(func: WasmModuleFunction): string {
    const modName = this.toPythonName(this.manifest().name);
    const args = func.inputs
      .filter((i) => i.required)
      .map((i) => `${i.name}="..."`)
      .join(', ');
    return `from modules import ${modName}\nresult = ${modName}.${func.name}(${args})`;
  }

  jsExample(func: WasmModuleFunction): string {
    const modName = this.toCamelCase(this.manifest().name);
    const jsFunc = this.toCamelCase(func.name);
    const args = func.inputs
      .filter((i) => i.required)
      .map((i) => `${this.toCamelCase(i.name)}: "..."`)
      .join(', ');
    return `const result = modules.${modName}.${jsFunc}({ ${args} });`;
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
